import { Alert, Button, IconButton, Input } from '@fullscreendigital/carrefour-dex-fe'
import { Grid, useMediaQuery } from '@mui/material'
import { Draw, ReportProblemOutlined } from '@mui/icons-material'
import { Modal } from 'components/core-ui/Modal'
import {
  getCurrentCollectingCompanies,
  getDailyReport,
  getDailyReportCurrentUserSignature,
  getJobTitleOptions
} from 'components/waste-management/api'
import {
  DailyReportItem,
  DailyReportPayloadItem,
  EditCreateModalProps
} from 'components/waste-management/interfaces'
import { useNotification } from 'hooks'
import { useEffect, useMemo, useRef, useState } from 'react'
import { getInitialValues, getValidationSchema } from './helpers'
import { useFormik } from 'formik'
import { makeCorePostRequest, makeCorePutRequest } from 'utils/api'
import { useMutation } from 'react-query'
import { handleQueryError } from 'utils/helpers'
import { AsyncAutocomplete } from 'components/core-ui/AsyncAutocomplete'
import s from 'components/core-ui/Modal/Modal.modules.scss'
import { toDailyReportPayload } from './serializers'
import { FormInfoItems } from 'components/core-ui/FormInfoItems'
import { getUserName, getUserStoreInfo } from 'utils/authService'
import ls from './DailyReportModal.modules.scss'
import { HYPER_STORE_TYPE } from 'components/waste-management/constants'
import { Signature } from './Signature'
import React from 'react'

const DailyReportModal = (props: EditCreateModalProps) => {
  const { isModalOpen, closeModal, isEdit, editItemId, shouldRefetchItem, setShouldRefreshData } =
    props

  const [currentStore, setCurrentStore] = useState(null)
  const [signatureError, setSignatureError] = useState('')
  const [hasSignatureChanged, setHasSignatureChanged] = useState(false)

  const isXs = useMediaQuery('(max-width:624px)')

  const sigRef = useRef(null)

  useEffect(() => {
    setCurrentStore(getUserStoreInfo())
  }, [])

  const { showNotification } = useNotification()

  const { data: editItem } = getDailyReport(showNotification, editItemId, shouldRefetchItem)

  const { data: signature } = getDailyReportCurrentUserSignature(
    showNotification,
    editItemId,
    editItem ? editItem.assignees.find(assignee => assignee.name === getUserName())?.id : null,
    shouldRefetchItem
  )

  const { data: currentCollectingCompanies } = getCurrentCollectingCompanies(showNotification)

  const { data: jobTitleData } = getJobTitleOptions(showNotification, currentStore?.storeADId)

  const currentUserName = useMemo(() => {
    return getUserName()
  }, [])

  const currentEmployeeId = useMemo(
    () => editItem?.assignees.find(assignee => assignee.name === currentUserName)?.id,
    [editItem, currentUserName]
  )

  const companies = useMemo(() => {
    if (editItem) {
      return (
        editItem.wasteCategoryDailyReports?.map(report => ({
          wasteCompanyName: report.wasteCompanyName,
          name: report.wasteCompanyName,
          grossWeightMeti: report.wasteCategoryCalculatedWeight
        })) ?? []
      )
    }
    return (
      currentCollectingCompanies?.list?.map(company => ({
        id: company.id,
        name: company.name,
        grossWeightMeti: company.grossWeightMeti
      })) ?? []
    )
  }, [editItem, currentCollectingCompanies])

  const initialValues = useMemo(
    () => getInitialValues(editItem, jobTitleData?.storeType, jobTitleData?.jobTitles, companies),
    [editItem, jobTitleData?.storeType, jobTitleData?.jobTitles, companies]
  )

  const validationSchema = useMemo(
    () => getValidationSchema(jobTitleData?.storeType),
    [jobTitleData?.storeType]
  )

  const formik = useFormik<DailyReportItem>({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: async values => {
      mutate(toDailyReportPayload(values))
    }
  })

  const metiReportName = useMemo(
    () =>
      isEdit
        ? editItem?.metiReports.map(report => report.name).join(', ')
        : currentCollectingCompanies?.reports?.join(', ') ?? '',
    [isEdit, editItem?.metiReports, currentCollectingCompanies]
  )

  const mutateReport = async (values: DailyReportPayloadItem) => {
    const response = editItem
      ? await makeCorePutRequest(`/waste-management-system/daily-report/${editItem.id}`, values)
      : await makeCorePostRequest(`/waste-management-system/daily-report`, values)

    return response
  }

  const uploadSignature = async (reportId: number, employeeId: number) => {
    if (!sigRef.current || sigRef.current.isEmpty()) return

    const canvas = sigRef.current.getCanvas()

    const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/png'))

    const formData = new FormData()
    if (blob) {
      formData.append('signaturePicture', blob, 'signature.png')
    }

    await makeCorePostRequest(
      `/waste-management-system/daily-report/signature/${reportId}/${employeeId}`,
      formData
    )
  }

  const { isLoading, mutate } = useMutation(mutateReport, {
    onSuccess: async res => {
      const reportEmployeeId = res.assignees.find(assignee => assignee.name === currentUserName)?.id

      if (reportEmployeeId) {
        if (!sigRef.current.isEmpty()) {
          await uploadSignature(res.id || res.reportId, reportEmployeeId)
        }

        showNotification({
          message: `Raportul a fost ${editItem?.id ? 'editat' : 'creat'} cu succes.`,
          type: 'success'
        })
      } else {
        showNotification({
          message: `Raportul a fost ${
            editItem?.id ? 'editat' : 'creat'
          } cu succes, dar nu s-a putut salva semnatura.`,
          type: 'warning'
        })
      }

      setShouldRefreshData(true)
      closeModal()
      formik.resetForm()
      setHasSignatureChanged(false)
    },
    onError: error =>
      handleQueryError(
        error,
        `A aparut o eroare la ${editItem?.id ? 'editarea raportului.' : 'adaugarea raportului.'}`,
        showNotification
      )
  })

  const { isLoading: isSavingSignature, mutate: saveSignatureOnly } = useMutation(
    async () => {
      if (!editItem?.id || !currentEmployeeId) {
        throw new Error('Raportul sau utilizatorul nu sunt disponibili.')
      }
      if (!sigRef.current || sigRef.current.isEmpty()) {
        setSignatureError('Va rugam sa semnati inainte de a salva.')
        throw new Error('Empty signature')
      }
      await uploadSignature(editItem.id, currentEmployeeId)
    },
    {
      onSuccess: () => {
        setSignatureError('')
        showNotification({
          message: 'Semnatura a fost salvata cu succes.',
          type: 'success'
        })
        setShouldRefreshData(true)
        closeModal()
        formik.resetForm()
        setHasSignatureChanged(false)
      },
      onError: error => {
        if ((error as Error)?.message === 'Empty signature') return
        handleQueryError(error, 'A aparut o eroare la salvarea semnaturii.', showNotification)
      }
    }
  )

  const cancelModal = () => {
    closeModal()
    formik.resetForm()
    setHasSignatureChanged(false)
    setSignatureError('')
  }

  const matchingAssigneeIndex = useMemo(() => {
    if (currentUserName.trim() === formik.values.assignee1Name.trim()) return 1
    if (currentUserName.trim() === formik.values.assignee2Name.trim()) return 2
    if (
      jobTitleData?.storeType === HYPER_STORE_TYPE &&
      currentUserName.trim() === formik.values.assignee3Name.trim()
    )
      return 3
    return null
  }, [
    currentUserName,
    formik.values.assignee1Name,
    formik.values.assignee2Name,
    formik.values.assignee3Name,
    jobTitleData?.storeType
  ])

  const isCurrentUserAssignee = matchingAssigneeIndex !== null

  const showSaveSignatureButton = isEdit && isCurrentUserAssignee && !!currentEmployeeId

  const handleSignatureChange = (changed: boolean) => {
    setHasSignatureChanged(changed)
    if (changed) setSignatureError('')
  }

  const handleFormSubmit = () => {
    if (isCurrentUserAssignee && (!sigRef.current || sigRef.current.isEmpty())) {
      setSignatureError('Va rugam sa semnati inainte de a salva.')
      return
    }
    setSignatureError('')
    formik.handleSubmit()
  }

  return (
    <Modal
      isModalOpen={isModalOpen}
      closeModal={cancelModal}
      modalTitle={isEdit ? 'Editeaza raport zilnic' : 'Adauga raport zilnic'}
      modalWidth="60%"
    >
      <Grid container spacing={4}>
        <Grid xs={12} item>
          <FormInfoItems
            items={[
              {
                label: 'Locatie:',
                name: currentStore?.storeName,
                itemStyle: { gridTemplateColumns: '60px 1fr' }
              }
            ]}
          />
        </Grid>
        {isEdit && (
          <Grid xs={12} item>
            <Alert
              severity="warning"
              style={{
                fontSize: isXs ? '14px' : '18px',
                padding: isXs ? '8px 12px' : '16px 20px'
              }}
              icon={<ReportProblemOutlined sx={{ fontSize: isXs ? '24px' : '56px' }} />}
            >
              Atentie, daca se modifica datele pentru raportul curent, se vor sterge toate
              semnaturile existente!
            </Alert>
          </Grid>
        )}
        <Grid xs={12} md={6} item>
          <Input
            type="text"
            name="assignee1Name"
            label="Persoana responsabila 1"
            fullWidth
            variant="outlined"
            value={formik.values.assignee1Name}
            onChange={event => formik.setFieldValue('assignee1Name', event.currentTarget.value)}
            error={!!formik.errors.assignee1Name}
            helperText={formik.errors.assignee1Name as string}
            required
            disabled={!isEdit}
          />
        </Grid>
        <Grid xs={12} md={6} item>
          <AsyncAutocomplete
            variant="outlined"
            name="assignee1Function"
            label="Functie persoana responsabila 1"
            value={formik.values.assignee1Function}
            options={jobTitleData?.jobTitles || []}
            onChange={value => formik.setFieldValue('assignee1Function', value)}
            error={!!formik.errors.assignee1Function}
            helperText={formik.errors.assignee1Function?.value as string}
            required
          />
        </Grid>
        {matchingAssigneeIndex === 1 && (
          <>
            <Grid xs={12} md={8} item>
              <Signature
                signatureRef={sigRef}
                signatureError={signatureError}
                initialImage={signature || null}
                onSignatureChange={handleSignatureChange}
              />
            </Grid>
            <Grid xs={0} md={4} item></Grid>
          </>
        )}
        <Grid xs={12} md={6} item>
          <Input
            type="text"
            name="assignee2Name"
            label="Persoana responsabila 2"
            fullWidth
            variant="outlined"
            value={formik.values.assignee2Name}
            onChange={event => formik.setFieldValue('assignee2Name', event.currentTarget.value)}
            error={!!formik.errors.assignee2Name}
            helperText={formik.errors.assignee2Name as string}
            required
          />
        </Grid>
        <Grid xs={12} md={6} item>
          <AsyncAutocomplete
            variant="outlined"
            name="assignee2Function"
            label="Functie persoana responsabila 2"
            value={formik.values.assignee2Function}
            options={jobTitleData?.jobTitles || []}
            onChange={value => formik.setFieldValue('assignee2Function', value)}
            error={!!formik.errors.assignee2Function}
            helperText={formik.errors.assignee2Function?.value as string}
            required
          />
        </Grid>
        {matchingAssigneeIndex === 2 && (
          <>
            <Grid xs={12} md={8} item>
              <Signature
                signatureRef={sigRef}
                signatureError={signatureError}
                initialImage={signature || null}
                onSignatureChange={handleSignatureChange}
              />
            </Grid>
            <Grid xs={0} md={4} item></Grid>
          </>
        )}
        {jobTitleData?.storeType === HYPER_STORE_TYPE && (
          <>
            <Grid xs={12} md={6} item>
              <Input
                type="text"
                name="assignee3Name"
                label="Persoana responsabila 3"
                fullWidth
                variant="outlined"
                value={formik.values.assignee3Name}
                onChange={event => formik.setFieldValue('assignee3Name', event.currentTarget.value)}
                error={!!formik.errors.assignee3Name}
                helperText={formik.errors.assignee3Name as string}
                required
              />
            </Grid>
            <Grid xs={12} md={6} item>
              <AsyncAutocomplete
                variant="outlined"
                name="assignee3Function"
                label="Functie persoana responsabila 3"
                value={formik.values.assignee3Function}
                options={jobTitleData?.jobTitles || []}
                onChange={value => formik.setFieldValue('assignee3Function', value)}
                error={!!formik.errors.assignee3Function}
                helperText={formik.errors.assignee3Function?.value as string}
                required
              />
            </Grid>
            {matchingAssigneeIndex === 3 && (
              <>
                <Grid xs={12} md={8} item>
                  <Signature
                    signatureRef={sigRef}
                    signatureError={signatureError}
                    initialImage={signature || null}
                    onSignatureChange={handleSignatureChange}
                  />
                </Grid>
                <Grid xs={0} md={4} item></Grid>
              </>
            )}
          </>
        )}
        {!metiReportName && (
          <Grid xs={12} item>
            <Alert severity="error">
              Nu exista inca date din METI pentru a putea crea un raport zilnic.
            </Alert>
          </Grid>
        )}
        <Grid xs={12} item className={ls.disabledInputContainer}>
          <Input
            type="text"
            name="metiReportName"
            label="Raport METI"
            fullWidth
            variant="outlined"
            value={metiReportName}
            disabled
          />
        </Grid>
        {companies.map((company, index) =>
          !company.grossWeightMeti ? null : (
            <React.Fragment key={index}>
              <Grid xs={12} md={4} item className={ls.disabledInputContainer}>
                <Input
                  type="text"
                  name="company"
                  label="Prestator"
                  fullWidth
                  variant="outlined"
                  value={company.name}
                  disabled
                />
              </Grid>
              <Grid xs={12} md={4} item className={ls.disabledInputContainer}>
                <Input
                  type="text"
                  name="metiReportWeight"
                  label="Greutate calculata (kg)"
                  fullWidth
                  variant="outlined"
                  value={company.grossWeightMeti}
                  disabled
                />
              </Grid>
              <Grid xs={12} md={4} item>
                <Input
                  type="number"
                  name="actualWeight"
                  label="Greutate cantarita (kg)"
                  fullWidth
                  variant="outlined"
                  value={formik.values.wasteCompanyList[index]?.actualWeight || ''}
                  onChange={event =>
                    formik.setFieldValue(
                      'wasteCompanyList',
                      formik.values.wasteCompanyList.map((item, i) =>
                        i === index ? { ...item, actualWeight: event.currentTarget.value } : item
                      )
                    )
                  }
                  error={!!(formik.errors.wasteCompanyList?.[index] as any)?.actualWeight}
                  helperText={(formik.errors.wasteCompanyList?.[index] as any)?.actualWeight}
                  required
                />
              </Grid>
              {formik.values.wasteCompanyList[index]?.actualWeight !== '' &&
                +formik.values.wasteCompanyList[index]?.actualWeight > company.grossWeightMeti && (
                  <Grid xs={12} item>
                    <Alert severity="error">
                      Atentie! Greutatea cantarita este mai mare decat cea din METI. Sunteti sigur
                      ca greutatea introdusa este corecta?
                    </Alert>
                  </Grid>
                )}
            </React.Fragment>
          )
        )}
        <Grid xs={12} item className={s.modalButtons}>
          <Button
            variant="outlined"
            label="Anuleaza"
            onClick={cancelModal}
            disabled={isLoading || isSavingSignature}
          />
          {showSaveSignatureButton && hasSignatureChanged && !formik.dirty && (
            <IconButton
              variant="outlined"
              startIcon={<Draw />}
              label="Salveaza semnatura"
              onClick={() => saveSignatureOnly()}
              disabled={isLoading || isSavingSignature}
            />
          )}
          {formik.dirty && (
            <Button
              variant="contained"
              type="submit"
              label="Salveaza modificarile"
              onClick={handleFormSubmit}
              disabled={isLoading || isSavingSignature || !metiReportName}
            />
          )}
        </Grid>
      </Grid>
    </Modal>
  )
}

export default DailyReportModal
